import {
  DrawerBody,
  DrawerOverlay,
  Flex,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Decimal } from "@syla/shared/decimal";
import {
  isNotNullable,
  withoutNullable,
} from "@syla/shared/types/helpers/withoutNullable";
import { TransactionSourceIdType } from "@syla/shared/types/models/TransactionBase";
import {
  NewLedger,
  PatchLedger,
} from "@syla/shared/types/requests/TransactionRequests";
import { TransactionEvent } from "@syla/shared/types/responses/TransactionEvent";
import React, {
  Dispatch,
  SetStateAction,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { useForm, FormProvider } from "react-hook-form";
import { PartialDeep } from "type-fest";
import {
  getAggregatedLedgerTaxOutcomes,
  TaxOutcomeDetail,
} from "../../../../../helper/aggregateLedgerTaxOutcomes";
import { isProduction } from "../../../../../helper/environment";
import { getDirtyValues } from "../../../../../helper/forms/getDirtyValues";
import { adjustAmount } from "../../../../../helper/forms/stringAsNumberInputField";
import {
  BalanceDetail,
  getBalanceDetailsFromLedgers,
} from "../../../../../helper/ledger/aggregateBalanceInLedgers";
import { splitTransactionLedgers } from "../../../../../helper/ledger/splitTransactionLedgers";
import { getTransactionIconAndText } from "../../../../../helper/transaction/getTransactionIconAndText";
import { useGetWalletOptions } from "../../../../../hooks/wallet/useGetWalletOptions";
import { updateTransaction } from "../../../../../store/actions/updateTransaction";
import { useCurrentAccountStore } from "../../../../../store/currentAccountStore";
import { useTransactionsStore } from "../../../../../store/transactionsStore";
import {
  EditTransactionForm,
  LedgerForm,
} from "../../../../../types/order/addOrderForm";
import { AppDrawer } from "../../../../AppDrawer";
import { AppDrawerContent } from "../../../../modals/AppDrawerContent";
import { errorToastOptions } from "../../../../molecules/errorToastOptions";
import { renderTransactionNeedsReviewItems } from "./renderTransactionNeedsReviewItems";
import { TransactionBalancesSection } from "./TransactionBalancesSections";
import { TransactionDetailsSection } from "./TransactionDetailsSection";
import { TransactionLedgersSection } from "./TransactionLedgersSection";
import { TransactionOverviewHeader } from "./TransactionOverviewHeader";
import { TransactionOverviewLedgerEntry } from "./TransactionOverviewLedgerEntry";
import { TransactionSuggestionsSection } from "./TransactionSuggestionsSection";
import { TransactionTaxSection } from "./TransactionTaxSection";

export const TransactionOverview = ({
  transactionId,
  onClose,
  setCanDefaultCloseDrawer,
  isOpen,
  onCloseComplete,
  canDefaultCloseDrawer,
}: {
  transactionId: string;
  onClose: () => void;
  setCanDefaultCloseDrawer: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
  onCloseComplete: () => void;
  canDefaultCloseDrawer: boolean;
}): JSX.Element => {
  const transactionState = useTransactionsStore(
    (store) => store.transactions?.[transactionId],
  );

  if (!transactionState) {
    throw new Error(`Transaction ${transactionId} should be set`);
  }

  const transaction = transactionState.data;

  const updating = transactionState.updating;

  const isRefreshing = useTransactionsStore((state) => state.refreshing);

  const needsReviewItems = useMemo(
    () =>
      renderTransactionNeedsReviewItems({
        transactions: [transaction],
      }),
    [transaction],
  );

  // block close while updating
  useEffect(() => {
    setCanDefaultCloseDrawer((current) => {
      const next = !updating;
      return current === next ? current : next;
    });
  }, [setCanDefaultCloseDrawer, updating]);

  const toast = useToast();
  useEffect(() => {
    if (transactionState.error)
      toast({
        ...errorToastOptions,
        title: "Transaction update failed",
        description: transactionState.error,
      });
  }, [toast, transactionState.error]);

  const { icon, text } = useMemo(
    () => getTransactionIconAndText(transaction),
    [transaction],
  );

  const ledgers = useMemo(() => getDisplayLedgers(transaction), [transaction]);

  const taxOutcomeDetails: TaxOutcomeDetail[] = useMemo(() => {
    return getAggregatedLedgerTaxOutcomes(transaction.ledgers);
  }, [transaction.ledgers]);

  const balanceDetails: BalanceDetail[] = useMemo(() => {
    return getBalanceDetailsFromLedgers(transaction.ledgers);
  }, [transaction.ledgers]);

  // although it could have multiple transaction hashes, we only support show / edit one now
  const transactionHashSourceId = useMemo(() => {
    return transaction.sourceIds?.find(
      (sourceId) => sourceId.type === TransactionSourceIdType.TransactionHash,
    );
  }, [transaction.sourceIds]);

  const transactionHash = transactionHashSourceId?.id;

  const transactionHashDataSourceCode = transactionHashSourceId?.dataSourceCode;

  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const walletOptions = useGetWalletOptions(accountId)?.data;

  const {
    isOpen: editMode,
    onOpen: onOpenEditMode,
    onClose: onCloseEditMode,
  } = useDisclosure();

  const formValues = useMemo(
    () => getTransactionFormValues(transaction),
    [transaction],
  );

  const form = useForm<EditTransactionForm>({
    values: formValues,
    resetOptions: {
      keepDirtyValues: true,
      keepErrors: true,
    },
  });

  const {
    handleSubmit,
    formState: { isDirty, dirtyFields },
    reset,
  } = form;

  const handleCloseEditMode = useCallback(() => {
    // complete form reset back to defaults when edit mode is closed
    reset(formValues, {
      keepDirtyValues: false,
      keepErrors: false,
    });
    onCloseEditMode();
  }, [formValues, reset, onCloseEditMode]);

  const onSubmit = useCallback(
    async (data: EditTransactionForm) => {
      // turn off edit mode and early return if form is not dirty or no data
      if (!isDirty || !data) {
        handleCloseEditMode();
        return;
      }

      const dirtyValues = getDirtyValues(dirtyFields, data);

      if (!isProduction()) {
        console.debug({ dirtyFields, data, dirtyValues });
      }

      const mapPatchLedger = (
        ledger: PartialDeep<LedgerForm>,
        index: number,
      ): PatchLedger => {
        const fullLedger = data.updateLedgers[index];

        return {
          ...ledger,
          // the _id wasn't edited in the form, so we insert it here.
          _id: fullLedger._id!,
          amount:
            ledger.amount ?
              Decimal.from(adjustAmount(ledger.amount)).abs().toString()
            : undefined,
          marketValue:
            ledger.marketValue === undefined ?
              undefined
            : adjustAmount(ledger.marketValue),
        };
      };

      const mapNewLedger = (ledger: PartialDeep<LedgerForm>): NewLedger => ({
        // the b/e is responsible for creating the _id for new ledgers.
        type: ledger.type!,
        amount: Decimal.from(adjustAmount(ledger.amount)).abs().toString(),
        assetId: ledger.assetId!,
        customMarketValue:
          ledger.marketValue ?
            {
              assetId: undefined,
              marketValue: adjustAmount(ledger.marketValue),
            }
          : undefined,
      });

      const updateLedgers =
        dirtyValues.updateLedgers ?
          dirtyValues.updateLedgers
            .map((ledger, index) =>
              ledger ? mapPatchLedger(ledger, index) : undefined,
            )
            .filter(isNotNullable)
        : undefined;

      const newLedgers =
        dirtyValues.newLedgers ?
          withoutNullable(dirtyValues.newLedgers).map(mapNewLedger)
        : undefined;

      await updateTransaction({
        accountId,
        id: transaction._id,
        updates: {
          description: dirtyValues.description,
          transactionHash: dirtyValues.transactionHash,
          updateLedgers,
          newLedgers,
          date: dirtyValues.date,
          walletId: dirtyValues.walletId,
        },
      });

      // the call to updateTransaction also updates the store, so we pull
      // the latest state from the store and reset the form.
      const latestTransaction =
        useTransactionsStore.getState().transactions?.[transaction._id]?.data;

      if (!latestTransaction) {
        return;
      }

      reset(getTransactionFormValues(latestTransaction), {
        keepDirtyValues: false,
        keepDirty: false,
        keepErrors: false,
        keepTouched: false,
      });

      onCloseEditMode();
    },
    [
      accountId,
      isDirty,
      dirtyFields,
      reset,
      onCloseEditMode,
      handleCloseEditMode,
      transaction._id,
    ],
  );

  return (
    <AppDrawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      finalFocusRef={undefined}
      size="xl"
      closeOnOverlayClick={canDefaultCloseDrawer}
      closeOnEsc={canDefaultCloseDrawer}
    >
      <DrawerOverlay />
      <AppDrawerContent>
        <FormProvider {...form}>
          <TransactionOverviewHeader
            icon={icon}
            text={text}
            transaction={transaction}
            editMode={editMode}
            updating={!!updating}
            onClickBack={onClose}
            onOpenEditMode={onOpenEditMode}
            onCloseEditMode={handleCloseEditMode}
          />

          <DrawerBody px={0} py={0}>
            <form
              id="transaction-overview-form"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
            >
              <Flex direction="column" gap={8} pt={4} w="full">
                <TransactionSuggestionsSection
                  needsReviewItems={needsReviewItems}
                />

                <TransactionDetailsSection
                  transaction={transaction}
                  editMode={editMode}
                  updating={!!updating}
                  walletOptions={walletOptions}
                  transactionHash={transactionHash}
                  transactionHashDataSourceCode={transactionHashDataSourceCode}
                />

                <TransactionLedgersSection
                  editMode={editMode}
                  updating={!!updating}
                  ledgers={ledgers}
                />

                <TransactionBalancesSection balanceDetails={balanceDetails} />

                <TransactionTaxSection
                  taxOutcomeDetails={taxOutcomeDetails}
                  isLoading={isRefreshing}
                />
              </Flex>
            </form>
          </DrawerBody>
        </FormProvider>
      </AppDrawerContent>
    </AppDrawer>
  );
};

function getDisplayLedgers(
  transaction: TransactionEvent,
): TransactionOverviewLedgerEntry[] {
  const { inLedgers, outLedgers, feeLedgers } = splitTransactionLedgers([
    transaction,
  ]);

  return inLedgers
    .map<TransactionOverviewLedgerEntry>((ledger) => ({
      ledger,
      inLedger: true,
      feeLedger: false,
    }))
    .concat(
      outLedgers.map<TransactionOverviewLedgerEntry>((ledger) => ({
        ledger,
        inLedger: false,
        feeLedger: false,
      })),
    )
    .concat(
      feeLedgers.map<TransactionOverviewLedgerEntry>((ledger) => ({
        ledger,
        inLedger: false,
        feeLedger: true,
      })),
    );
}

function getTransactionFormValues(
  transaction: TransactionEvent,
): EditTransactionForm {
  const ledgers = getDisplayLedgers(transaction);

  const transactionHashSourceId = transaction.sourceIds?.find(
    (sourceId) => sourceId.type === TransactionSourceIdType.TransactionHash,
  );

  return {
    description: transaction.description,
    transactionHash: transactionHashSourceId?.id ?? "",
    updateLedgers: ledgers.map(({ ledger }) => ({
      _id: ledger._id,
      assetId: ledger.asset._id,
      amount: ledger.amount.abs().format().text,
      marketValue: ledger.marketValue ? ledger.marketValue.format().text : "",
      type: ledger.type,
      delete: false,
    })),
    newLedgers: [],
    date: transaction.date,
    walletId: transaction.wallet._id,
  };
}
